/**
 * Módulo de cotizaciones — CRUD, ítems, notas, revisiones, duplicación, PDF y envío por email
 */

import { createCrudMethods } from '../base/crudMethods';
import { downloadBlobFromEndpoint } from '../../utils/downloadHelpers';

/**
 * Crea el módulo de cotizaciones
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.authenticatedRequest - Función de request autenticada
 * @param {Function} deps.authenticatedFormDataRequest - Función para uploads y descargas
 * @returns {Object} Métodos del módulo cotizaciones
 */
export function createCotizacionesModule({ authenticatedRequest, authenticatedFormDataRequest }) {
  return {
    ...createCrudMethods(
      {
        endpoint: '/api/cotizaciones',
        entityName: 'cotizacion',
        entityNamePlural: 'cotizaciones',
        methodNames: { getById: 'getCotizacion' }
      },
      { authenticatedRequest }
    ),

    /**
     * Obtiene los items de una cotización
     *
     * @param {number} cotizacionId - ID de la cotización
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getCotizacionItems(cotizacionId) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/items`);
    },

    /**
     * Crea un nuevo item en una cotización
     *
     * @param {number} cotizacionId - ID de la cotización
     * @param {Object} data - Datos del item
     * @param {string} data.descripcion - Descripción del item
     * @param {number} data.cantidad - Cantidad
     * @param {number} data.precio_unitario - Precio unitario
     * @param {string} [data.unidad] - Unidad (hora, día, semana, etc.)
     * @param {string} [data.tipo] - Tipo de item
     * @param {number} [data.equipo_id] - ID del equipo (opcional)
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async createCotizacionItem(cotizacionId, data) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/items`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Actualiza un item de cotización
     *
     * @param {number} cotizacionId - ID de la cotización
     * @param {number} itemId - ID del item
     * @param {Object} data - Datos a actualizar
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async updateCotizacionItem(cotizacionId, itemId, data) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },

    /**
     * Elimina un item de cotización
     *
     * @param {number} cotizacionId - ID de la cotización
     * @param {number} itemId - ID del item
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async deleteCotizacionItem(cotizacionId, itemId) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/items/${itemId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Reordena items de una cotización en batch
     *
     * @param {number} cotizacionId - ID de la cotización
     * @param {Array<{id: number, orden: number}>} ordenArray - Nuevo orden de items
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async reordenarCotizacionItems(cotizacionId, ordenArray) {
      return await authenticatedRequest(
        `/api/cotizaciones/${cotizacionId}/items/reordenar`,
        {
          method: 'PUT',
          body: JSON.stringify(ordenArray)
        }
      );
    },

    /**
     * Obtiene las notas de una cotización
     *
     * @param {number} cotizacionId - ID de la cotización
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getCotizacionNotas(cotizacionId) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/notas`);
    },

    /**
     * Crea una nueva nota en una cotización
     *
     * @param {number} cotizacionId - ID de la cotización
     * @param {Object} data - Datos de la nota (tipo, contenido)
     * @returns {Promise<{success: boolean, data: Object, message: string}>}
     */
    async createCotizacionNota(cotizacionId, data) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/notas`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Actualiza una nota existente
     *
     * @param {number} cotizacionId - ID de la cotización
     * @param {number} notaId - ID de la nota
     * @param {Object} data - Datos a actualizar (tipo?, contenido?)
     * @returns {Promise<{success: boolean, data: Object, message: string}>}
     */
    async updateCotizacionNota(cotizacionId, notaId, data) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/notas/${notaId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },

    /**
     * Elimina una nota (soft delete)
     *
     * @param {number} cotizacionId - ID de la cotización
     * @param {number} notaId - ID de la nota
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async deleteCotizacionNota(cotizacionId, notaId) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/notas/${notaId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Crea una nueva revisión de una cotización
     *
     * @param {number} cotizacionId - ID de la cotización base
     * @returns {Promise<{success: boolean, data: Object, message: string}>}
     */
    async crearRevisionCotizacion(cotizacionId) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/nueva-revision`, {
        method: 'POST'
      });
    },

    /**
     * Obtiene todas las revisiones de una cotización
     *
     * @param {number} cotizacionId - ID de la cotización
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getRevisionesCotizacion(cotizacionId) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/revisiones`);
    },

    /**
     * Duplica una cotización como nueva cotización independiente en rev 0
     *
     * @param {number} cotizacionId - ID de la cotización a duplicar
     * @returns {Promise<{success: boolean, data: Object, message: string}>}
     */
    async duplicarCotizacion(cotizacionId) {
      return await authenticatedRequest(`/api/cotizaciones/${cotizacionId}/duplicate`, {
        method: 'POST'
      });
    },

    /**
     * Envía la cotización al email del cliente con PDF adjunto
     *
     * @param {number} cotizacionId - ID de la cotización
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async enviarCotizacionEmail(cotizacionId) {
      return await authenticatedRequest(
        `/api/cotizaciones/${cotizacionId}/enviar-email`,
        { method: 'POST' }
      );
    },

    /**
     * Descarga el PDF de una cotización (genera, sube a Storage y sirve el buffer)
     *
     * @param {number} cotizacionId - ID de la cotización
     * @returns {Promise<{blob: Blob, filename: string}>}
     */
    async generarPdfCotizacion(cotizacionId) {
      return downloadBlobFromEndpoint(
        authenticatedFormDataRequest,
        `/api/cotizaciones/${cotizacionId}/pdf`,
        `cotizacion-${cotizacionId}.pdf`
      );
    }
  };
}
